import { IconButton } from '@mui/material';
import type { InvoiceOverview } from '@weft/api-definition';
import { SYNC_STATUSES, type InvoiceBillingStatus } from '@weft/types';
import { isNotNullOrUndefined, validateNotNullOrUndefined } from '@weft/utils';
import { useConnectedIntegrations } from 'apps/app/src/hooks/requests/use-integrations';
import { USE_INVOICES_OVERVIEWS_QUERY_KEY } from 'apps/app/src/hooks/requests/use-invoices-overviews/use-invoices-overviews-consts';
import { useSyncNetSuiteInvoice } from 'apps/app/src/hooks/requests/use-net-suite';
import { useUpdateInvoicesBillingStatuses } from 'apps/app/src/hooks/requests/use-update-invoices-billing-statuses';
import { useActivate } from 'apps/app/src/hooks/use-activate';
import { runMutation } from 'apps/app/src/utils/vy-mutation';
import React from 'react';
import { useQueryClient } from 'react-query';
import { LoadingOverlay } from '../../../../../components/design/LoadingOverlay';
import { useSyncToStripe } from '../../../../../hooks/requests/use-sync-stripe';
import { LightInvoicesPdfDownloader } from '../../../LightInvoiceViewPage/light-invoices-pdf-downloader/LightInvoicesPdfDownloader';
import { getBillingStatusLabel } from '../InvoiceBillingStatusLabel';
import { InvoicesActionsMenu } from '../invoices-actions-menu/InvoicesActionsMenu';
import { MarkAsPaidDialog } from '../MarkAsPaidDialog/MarkAsPaidDialog';

type InvoiceOverviewTableDotMenuProps = {
  invoice: InvoiceOverview;
};

export const InvoiceOverviewTableDotMenu:React.FC<InvoiceOverviewTableDotMenuProps> = ({ invoice }) => {
  const [isDownloadingPdf, setDownloadingAsPdf, unsetDownloadingAsPdf] = useActivate(false);
  const [isDownloadingPdfStatement, setDownloadingAsPdfStatement, unsetDownloadingAsPdfStatement] = useActivate(false);
  const updateInvoicesBillingStatusesMutation = useUpdateInvoicesBillingStatuses();
  const [isMarkAsPaidDialogOpen, setMarkAsPaidDialogOpen, setMarkAsPaidDialogClose] = useActivate(false);
  const syncToNetSuiteMutation = useSyncNetSuiteInvoice();
  const syncInvoicesToStripeMutation = useSyncToStripe();

  const { data: connectedIntegrations } = useConnectedIntegrations({ suspense: true });
  validateNotNullOrUndefined(connectedIntegrations, 'connectedIntegrations');

  const queryClient = useQueryClient();

  const handleUpdateInvoicesBillingStatus = async (billingStatus: InvoiceBillingStatus) => {
    const displayBillingStatus = getBillingStatusLabel(billingStatus);

    await runMutation(
      updateInvoicesBillingStatusesMutation,
      { invoiceStatus: billingStatus, invoiceIds: [invoice.id] },
      {
        pending: `Updating invoice status to ${displayBillingStatus}...`,
        success: `Invoice status updated to ${displayBillingStatus}`,
        error: `Failed to update invoice status to ${displayBillingStatus}`,
      },
      {
        onSuccess: async () => {
          await queryClient.resetQueries({
            queryKey: [USE_INVOICES_OVERVIEWS_QUERY_KEY],
          });
        },
      },
    );
  };

  const handleSyncToNetSuite = () => syncToNetSuiteMutation.mutate({ invoicesIds: [invoice.id] });

  const handleSyncToStripe = async () => {
    await runMutation(
      syncInvoicesToStripeMutation,
      {
        invoiceIds: [invoice.id],
      },
      {
        pending: 'Syncing invoice to Stripe...',
        success: 'Invoice synced to Stripe',
        error: 'Failed to sync invoice to Stripe',
      },
    );
  };

  const invoiceApplicableForSync = isNotNullOrUndefined(invoice.syncStatus) && invoice.syncStatus !== SYNC_STATUSES.Synced;

  return (
    <>
      <LoadingOverlay show={isDownloadingPdf || isDownloadingPdfStatement} />

      {isDownloadingPdf && (
        <LightInvoicesPdfDownloader
          onFinishedDownloading={() => unsetDownloadingAsPdf()}
          invoicesIds={[invoice.id]}
        />
      )}
      {isDownloadingPdfStatement && (
        <LightInvoicesPdfDownloader
          onFinishedDownloading={() => unsetDownloadingAsPdfStatement()}
          invoicesIds={[invoice.id]}
          isStatement
        />
      )}
      {
        isMarkAsPaidDialogOpen && (
          <MarkAsPaidDialog
            open={isMarkAsPaidDialogOpen}
            invoiceId={invoice.id}
            onClose={() => setMarkAsPaidDialogClose()}
            customerName={invoice.customer.name}
          />
        )
      }
     
      <div
        onClick={(event: React.MouseEvent<HTMLDivElement> & { ignore?: boolean }) => {
          event.ignore = true;
        }}
        className="z-[9999] flex justify-center"
      >
        <InvoicesActionsMenu
          onDownloadAsPdf={() => setDownloadingAsPdf()}
          invoices={[invoice]}
          onDownloadAsPdfStatement={() => setDownloadingAsPdfStatement()}
          onUpdateInvoicesBillingStatus={handleUpdateInvoicesBillingStatus}
          onSyncToNetSuite={handleSyncToNetSuite}
          onSyncToStripe={handleSyncToStripe}
          connectedIntegrations={connectedIntegrations}
          syncToNetSuiteDisabled={!invoiceApplicableForSync}
          onMarkAsPaid={() => setMarkAsPaidDialogOpen()}
        >
          <IconButton className='aspect-square rounded-full'>
            <span
              className='bg-icon text-label mx-2 mb-[3px] inline-block h-1 w-1 rounded-full shadow-[0_7px_0_#ADC2E2,0_-7px_0_#ADC2E2]'
            />
          </IconButton>
        </InvoicesActionsMenu>
      </div>

    </>
  );
};